import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { OrderFormCategoryDTO } from 'support/types';

export type State = {
  productsHavePrice: boolean;
  subtotal: number;
  minAmount: number | undefined | null;
  numberOfItemsWithQuantity: number;
  currency: string | undefined | null;
  categories: Array<OrderFormCategoryDTO> | undefined | null;
  filteredCategories: Array<OrderFormCategoryDTO> | undefined | null;
  childProductIds: Set<string>;
};

export type Actions = {
  // Used to set productsHavePrice to true if any product has a price defined
  registerProductPrice: (productPrice: number | null) => void;
  setProductsHavePrice: (value: boolean) => void;
  setNumberOfItemsWithQuantity: (count: number) => void;
  setSubtotal: (subtotal: number) => void;
  setOrderFormData: (minAmount: number | undefined | null, currency: string | undefined | null) => void;
  setCategories: (categories: Array<OrderFormCategoryDTO>) => void;
  setFilteredCategories: (categories: Array<OrderFormCategoryDTO>) => void;
  setChildProductIds: (ids: Set<string>) => void;
  reset: () => void;
};

export const initialState: State = {
  productsHavePrice: false,
  subtotal: 0,
  minAmount: null,
  currency: null,
  categories: null,
  filteredCategories: null,
  childProductIds: new Set<string>(),
  numberOfItemsWithQuantity: 0,
};

export const useOrderFormStore = create(
  immer<State & Actions>((set) => ({
    ...initialState,

    setCategories: (categories: Array<OrderFormCategoryDTO>) => {
      set((state) => {
        state.categories = categories;
      });
    },

    setFilteredCategories: (categories: Array<OrderFormCategoryDTO>) => {
      set((state) => {
        state.filteredCategories = categories;
      });
    },

    setChildProductIds: (ids: Set<string>) => {
      set((state) => {
        state.childProductIds = ids;
      });
    },

    registerProductPrice: (productPrice: number | null) => {
      set((state) => {
        state.productsHavePrice = state.productsHavePrice || typeof productPrice === 'number';
      });
    },

    setProductsHavePrice: (value: boolean) => {
      set((state) => {
        state.productsHavePrice = value;
      });
    },

    setNumberOfItemsWithQuantity: (count: number) => {
      set((state) => {
        state.numberOfItemsWithQuantity = count;
      });
    },

    setSubtotal: (subtotal: number) => {
      set((state) => {
        state.subtotal = subtotal;
      });
    },

    setOrderFormData: (minAmount: number | undefined | null, currency: string | undefined | null) => {
      set((state) => {
        state.minAmount = minAmount;
        state.currency = currency;
      });
    },

    reset: () => {
      set((state) => {
        state.subtotal = initialState.subtotal;
        state.minAmount = initialState.minAmount;
        state.currency = initialState.currency;
        state.productsHavePrice = initialState.productsHavePrice;
      });
    },
  })),
);

export const initializeOrderFormCategories = (
  categories: Array<OrderFormCategoryDTO>,
  childProductIds?: Set<string>,
) => {
  useOrderFormStore.getState().setCategories(categories);
  useOrderFormStore.getState().setFilteredCategories(categories);
  if (childProductIds) {
    useOrderFormStore.getState().setChildProductIds(childProductIds);
  }
};

export const setFilteredCategories = (categories: Array<OrderFormCategoryDTO>) => {
  return useOrderFormStore.getState().setFilteredCategories(categories);
};

export const setSubtotal = (subtotal: number) => {
  return useOrderFormStore.getState().setSubtotal(subtotal);
};

export const setOrderFormData = (minAmount: number | undefined | null, currency: string | undefined | null) => {
  return useOrderFormStore.getState().setOrderFormData(minAmount, currency);
};

export const registerProductPrice = (productPrice: number | null) => {
  return useOrderFormStore.getState().registerProductPrice(productPrice);
};

export const setProductsHavePrice = (value: boolean) => {
  return useOrderFormStore.getState().setProductsHavePrice(value);
};

export const setNumberOfItemsWithQuantity = (count: number) => {
  return useOrderFormStore.getState().setNumberOfItemsWithQuantity(count);
};
